import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { ASSET_METADATA, ASSET_TYPE, Asset, AssetsUser, FIREARM_TYPE, Firearm, FirearmAssignmentStorageType, FirearmAssignmentType, FirearmLifecycleStatus, RefCode } from 'assets-model';
import { AssetService, FirearmDataSource, FirearmService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class FirearmSearchComponentBase extends CrudListComponent<Firearm, number> {
  protected systemConfig: any;

  reportRun: any = {};

  displayedColumns: string[] = [ 'asset.assetId', 'calibre', 'currentAssignmentEmail', 'currentAssignmentFirearmAssignmentStorageType', 'currentAssignmentFirearmAssignmentType', 'currentlyAssignable', 'currentlyAssigned', 'firearmPoolExisting', 'firearmType', 'importDataMap', 'lifecycleState', 'lockVersion', 'personalFirearm', 'poolFirearm', 'suspended', 'actions'];

  public searchForm: FormGroup;

  assetFilter: Asset;
  assetSelectionList: Asset[];
  @ViewChild('assetFilterInput') assetFilterInput: MatSelect;

  calibreSelectionList: RefCode[];
  calibreFilter: string;
  @ViewChild('calibreFilterInput') calibreFilterInput: MatSelect;

  currentAssignmentEmailFilter: string;
  @ViewChild('currentAssignmentEmailFilterInput') currentAssignmentEmailFilterInput: ElementRef;

  currentAssignmentFirearmAssignmentStorageTypeSelectionList: RefCode[];
  currentAssignmentFirearmAssignmentStorageTypeFilter: FirearmAssignmentStorageType;
  @ViewChild('currentAssignmentFirearmAssignmentStorageTypeFilterInput') currentAssignmentFirearmAssignmentStorageTypeFilterInput: MatSelect;

  currentAssignmentFirearmAssignmentTypeSelectionList: RefCode[];
  currentAssignmentFirearmAssignmentTypeFilter: FirearmAssignmentType;
  @ViewChild('currentAssignmentFirearmAssignmentTypeFilterInput') currentAssignmentFirearmAssignmentTypeFilterInput: MatSelect;

  currentlyAssignableFilter: boolean;
  @ViewChild('currentlyAssignableFilterInput') currentlyAssignableFilterInput: MatSelect;

  currentlyAssignedFilter: boolean;
  @ViewChild('currentlyAssignedFilterInput') currentlyAssignedFilterInput: MatSelect;

  firearmTypeSelectionList: RefCode[];
  firearmTypeFilter: string;
  @ViewChild('firearmTypeFilterInput') firearmTypeFilterInput: MatSelect;

  lifecycleStateSelectionList: RefCode[];
  lifecycleStateFilter: FirearmLifecycleStatus;
  @ViewChild('lifecycleStateFilterInput') lifecycleStateFilterInput: MatSelect;

  personalFirearmFilter: boolean;
  @ViewChild('personalFirearmFilterInput') personalFirearmFilterInput: MatSelect;

  poolFirearmFilter: boolean;
  @ViewChild('poolFirearmFilterInput') poolFirearmFilterInput: MatSelect;

  suspendedFilter: boolean;
  @ViewChild('suspendedFilterInput') suspendedFilterInput: MatSelect;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected firearmService: FirearmService,
    protected assetService: AssetService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(FIREARM_TYPE, route, router, userStorage, dialog, firearmService, injector);

    this.searchForm = this.fb.group({
      asset: [null],
      calibre: [null],
      currentAssignmentEmail: [null],
      currentAssignmentFirearmAssignmentStorageType: [null],
      currentAssignmentFirearmAssignmentType: [null],
      currentlyAssignable: [null],
      currentlyAssigned: [null],
      firearmType: [null],
      lifecycleState: [null],
      personalFirearm: [null],
      poolFirearm: [null],
      suspended: [null],
      outputFormat: [null],
      download: [null]
    });
  }

  createDataSource(): CrudDataSource<Firearm> {
    return new FirearmDataSource(this.firearmService);
  }

  createServiceArguments(): any {
    return {
      assetId: this.assetFilter && this.assetFilter.assetId,
      calibre: this.calibreFilter,
      currentAssignmentEmail: this.currentAssignmentEmailFilter,
      currentAssignmentFirearmAssignmentStorageType: this.currentAssignmentFirearmAssignmentStorageTypeFilter,
      currentAssignmentFirearmAssignmentType: this.currentAssignmentFirearmAssignmentTypeFilter,
      currentlyAssignable: this.currentlyAssignableFilter,
      currentlyAssigned: this.currentlyAssignedFilter,
      firearmType: this.firearmTypeFilter,
      lifecycleState: this.lifecycleStateFilter,
      personalFirearm: this.personalFirearmFilter,
      poolFirearm: this.poolFirearmFilter,
      suspended: this.suspendedFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.assetFilter = null;
    this.calibreFilter = null;
    this.currentAssignmentEmailFilter = null;
    this.currentAssignmentFirearmAssignmentStorageTypeFilter = null;
    this.currentAssignmentFirearmAssignmentTypeFilter = null;
    this.currentlyAssignableFilter = null;
    this.currentlyAssignedFilter = null;
    this.firearmTypeFilter = null;
    this.lifecycleStateFilter = null;
    this.personalFirearmFilter = null;
    this.poolFirearmFilter = null;
    this.suspendedFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedSelectEventListener(this.assetFilterInput);
    this.addSearchOnChangedSelectEventListener(this.calibreFilterInput);
    this.addSearchOnChangedInputEventListener(this.currentAssignmentEmailFilterInput);
    this.addSearchOnChangedSelectEventListener(this.currentAssignmentFirearmAssignmentStorageTypeFilterInput);
    this.addSearchOnChangedSelectEventListener(this.currentAssignmentFirearmAssignmentTypeFilterInput);
    this.addSearchOnChangedSelectEventListener(this.currentlyAssignableFilterInput);
    this.addSearchOnChangedSelectEventListener(this.currentlyAssignedFilterInput);
    this.addSearchOnChangedSelectEventListener(this.firearmTypeFilterInput);
    this.addSearchOnChangedSelectEventListener(this.lifecycleStateFilterInput);
    this.addSearchOnChangedSelectEventListener(this.personalFirearmFilterInput);
    this.addSearchOnChangedSelectEventListener(this.poolFirearmFilterInput);
    this.addSearchOnChangedSelectEventListener(this.suspendedFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();

    this.firearmService.exportReportSpecification().subscribe(spec => {
      if (!spec) {
        return;
      }
      this.reportRun.outputFormats = spec.outputFormatsSupported;
      this.reportRun.outputFormat = this.reportRun.outputFormats.find(s => s.name == "XLS") || spec.outputFormatsSupported[0];
      this.reportRun.download = !!this.reportRun.outputFormat.downloadOnly;
    });
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
      config.export = config.export || {};
      config.export.options = config.export.options || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      asset: (value: Asset) => { self.assetFilter = value; this.assetChanged(value, false); },
      calibre: (value: string) => self.calibreFilter = value,
      currentAssignmentEmail: (value: string) => self.currentAssignmentEmailFilter = value,
      currentAssignmentFirearmAssignmentStorageType: (value: FirearmAssignmentStorageType) => self.currentAssignmentFirearmAssignmentStorageTypeFilter = value,
      currentAssignmentFirearmAssignmentType: (value: FirearmAssignmentType) => self.currentAssignmentFirearmAssignmentTypeFilter = value,
      currentlyAssignable: (value: boolean) => self.currentlyAssignableFilter = value,
      currentlyAssigned: (value: boolean) => self.currentlyAssignedFilter = value,
      firearmType: (value: string) => self.firearmTypeFilter = value,
      lifecycleState: (value: FirearmLifecycleStatus) => self.lifecycleStateFilter = value,
      personalFirearm: (value: boolean) => self.personalFirearmFilter = value,
      poolFirearm: (value: boolean) => self.poolFirearmFilter = value,
      suspended: (value: boolean) => self.suspendedFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }
  
  protected getExportConfig(): any {
    if (this.systemConfig && this.systemConfig.export && this.systemConfig.export.options) {
      return this.systemConfig.export.options;
    }
    return {};
  }
  
  protected getMaxExportResults(): number {
    return this.getExportConfig() && this.getExportConfig().maxExportResults;
  }

  isExportEnabled(): boolean {
    return this.reportRun && this.reportRun.download != null && this.reportRun.outputFormat != null
      && this.pageInfo && this.pageInfo.totalElements != null && this.pageInfo.totalElements > 0
      && (this.getMaxExportResults() == null || this.pageInfo.totalElements <= this.getMaxExportResults());
  }

  exportAction() {
    return this.firearmService.exportByFullFiltersAction(this.createServiceArguments(),
      this.reportRun.outputFormat, this.reportRun.download, this.pageSpecification());
  }

  assetLoadSelectionList() {
    this.assetService.findAll().subscribe(
      data => {
        this.assetSelectionList = data.items;
      }
    );
  }
  
  assetDisplay(item: Asset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE.description(item);
  }
  
  assetChanging(value: Asset, unsetDependents: boolean = true) {}
  
  assetChanged(value: Asset, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  assetCompare(a: any, b: any) {
    return ASSET_METADATA.equals(a, b);
  }
  
  

  calibreLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_CALIBRE').subscribe(
      data => {
        this.calibreSelectionList = data;
      }
    );
  }

  calibreChanging(value: string, unsetDependents: boolean = true) {}
  
  calibreChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  currentAssignmentEmailChanging(value: string, unsetDependents: boolean = true) {}
  
  currentAssignmentEmailChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  currentAssignmentFirearmAssignmentStorageTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_STORAGE_TYPE').subscribe(
      data => {
        this.currentAssignmentFirearmAssignmentStorageTypeSelectionList = data;
      }
    );
  }

  currentAssignmentFirearmAssignmentStorageTypeChanging(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {}
  
  currentAssignmentFirearmAssignmentStorageTypeChanged(value: FirearmAssignmentStorageType, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  currentAssignmentFirearmAssignmentTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_ASSIGNMENT_TYPE').subscribe(
      data => {
        this.currentAssignmentFirearmAssignmentTypeSelectionList = data;
      }
    );
  }

  currentAssignmentFirearmAssignmentTypeChanging(value: FirearmAssignmentType, unsetDependents: boolean = true) {}
  
  currentAssignmentFirearmAssignmentTypeChanged(value: FirearmAssignmentType, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  currentlyAssignableChanging(value: boolean, unsetDependents: boolean = true) {}
  
  currentlyAssignableChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  currentlyAssignedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  currentlyAssignedChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  firearmTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_TYPE').subscribe(
      data => {
        this.firearmTypeSelectionList = data;
      }
    );
  }

  firearmTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  firearmTypeChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  lifecycleStateLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_LIFECYCLE_STATE').subscribe(
      data => {
        this.lifecycleStateSelectionList = data;
      }
    );
  }

  lifecycleStateChanging(value: FirearmLifecycleStatus, unsetDependents: boolean = true) {}
  
  lifecycleStateChanged(value: FirearmLifecycleStatus, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  personalFirearmChanging(value: boolean, unsetDependents: boolean = true) {}
  
  personalFirearmChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  poolFirearmChanging(value: boolean, unsetDependents: boolean = true) {}
  
  poolFirearmChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  suspendedChanging(value: boolean, unsetDependents: boolean = true) {}
  
  suspendedChanged(value: boolean, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get assetId() {
    return this.searchForm && this.searchForm.get('assetId');
  }

  get asset() {
    return this.searchForm && this.searchForm.get('asset');
  }

  get calibre() {
    return this.searchForm && this.searchForm.get('calibre');
  }

  get currentAssignmentEmail() {
    return this.searchForm && this.searchForm.get('currentAssignmentEmail');
  }

  get currentAssignmentFirearmAssignmentStorageType() {
    return this.searchForm && this.searchForm.get('currentAssignmentFirearmAssignmentStorageType');
  }

  get currentAssignmentFirearmAssignmentType() {
    return this.searchForm && this.searchForm.get('currentAssignmentFirearmAssignmentType');
  }

  get currentlyAssignable() {
    return this.searchForm && this.searchForm.get('currentlyAssignable');
  }

  get currentlyAssigned() {
    return this.searchForm && this.searchForm.get('currentlyAssigned');
  }

  get firearmType() {
    return this.searchForm && this.searchForm.get('firearmType');
  }

  get lifecycleState() {
    return this.searchForm && this.searchForm.get('lifecycleState');
  }

  get personalFirearm() {
    return this.searchForm && this.searchForm.get('personalFirearm');
  }

  get poolFirearm() {
    return this.searchForm && this.searchForm.get('poolFirearm');
  }

  get suspended() {
    return this.searchForm && this.searchForm.get('suspended');
  }


}



